export const CHUNKED_FILE_READER_EVENTS = {
    BEGIN: 'begin',
    PROGRESS: 'progress',
    CHUNK: 'chunk',
    END: 'end'
};

export class ChunkedFileReader {
    constructor(opts = {}) {
        this.maxChunkSize = opts.maxChunkSize || 1024 * 1024;
        // Smaller chunk size for Safari to improve performance
        if (this.isSafari()) {
            this.maxChunkSize = Math.min(this.maxChunkSize, 1024 * 256); // 256KB for Safari
        }
        this.listeners = {};
        this.concurrentReads =
            opts.concurrentReads || (this.isSafari() ? 1 : 3); // Limit concurrent reads in Safari
    }

    // Detect if browser is Safari
    isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    subscribe(eventName, listener, thisObj) {
        if (!eventName || !listener) {
            // eslint-disable-next-line i18next/no-literal-string
            throw new Error('Event name and listener must be provided.');
        }

        this.listeners[eventName] = this.listeners[eventName] || [];
        this.listeners[eventName].push({
            ctx: thisObj,
            fun: listener
        });
    }

    publish(eventName, eventArgs) {
        (this.listeners[eventName] || []).forEach((listener) =>
            listener.fun.call(listener.ctx, eventArgs)
        );
    }

    readChunks(input) {
        let chunkSize = Math.min(this.maxChunkSize, input.size);
        let remainingBytes = input.size;
        const numberOfChunks = Math.ceil(remainingBytes / chunkSize);

        let position = 0;
        let sequence = 1;
        let activeReaders = 0;
        let chunksProcessed = 0;

        this.publish(CHUNKED_FILE_READER_EVENTS.BEGIN, {
            nchunks: numberOfChunks
        });

        // Create a function to configure a FileReader with the appropriate onload handler
        const createFileReader = (currentSequence) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target.readyState !== FileReader.DONE) {
                    activeReaders--;
                    return;
                }

                chunksProcessed++;

                this.publish(CHUNKED_FILE_READER_EVENTS.PROGRESS, {
                    nchunks: numberOfChunks,
                    done: chunksProcessed,
                    done_ratio: chunksProcessed / numberOfChunks
                });

                this.publish(CHUNKED_FILE_READER_EVENTS.CHUNK, {
                    seq: currentSequence,
                    nchunks: numberOfChunks,
                    chunk: e.target.result
                });

                activeReaders--;

                // If all chunks have been processed, publish END event
                if (chunksProcessed === numberOfChunks) {
                    this.publish(CHUNKED_FILE_READER_EVENTS.END, {
                        nchunks: numberOfChunks
                    });
                } else if (
                    remainingBytes > 0 ||
                    activeReaders < this.concurrentReads
                ) {
                    // Process more chunks if there are any left or if we can handle more concurrent reads
                    processNextChunks();
                }
            };
            return reader;
        };

        const processNextChunks = () => {
            // Process multiple chunks concurrently up to the concurrentReads limit
            while (remainingBytes > 0 && activeReaders < this.concurrentReads) {
                const currentPosition = position;
                const currentSequence = sequence;
                const currentChunkSize = Math.min(chunkSize, remainingBytes);

                const reader = createFileReader(currentSequence);
                activeReaders++;

                const blob = input.slice(
                    currentPosition,
                    currentPosition + currentChunkSize
                );
                reader.readAsArrayBuffer(blob);

                position += currentChunkSize;
                remainingBytes -= currentChunkSize;
                sequence++;
            }
        };

        // Start processing chunks
        processNextChunks();
    }
}
